import { ExternalLink, type PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { MaintenanceConfigurationResource, Repository } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";
import { PageContent } from "~/components/PageContent/PageContent";
import { Section } from "~/components/Section/Section";
import { Note } from "~/components/form";
export interface MaintenancePageLoaderData {
    configuration: MaintenanceConfigurationResource;
}
export async function maintenancePageLoader(repository: Repository): Promise<MaintenancePageLoaderData> {
    const configuration = await repository.MaintenanceConfiguration.get();
    return { configuration };
}
export interface MaintenancePageProps {
    loaderData: MaintenancePageLoaderData;
}
export function MaintenancePage({ loaderData }: MaintenancePageProps) {
    const [configuration, setConfiguration] = useState<MaintenanceConfigurationResource>(loaderData.configuration);
    const { execute: toggleMaintenanceMode, isExecuting } = useMutation({
        name: "Toggle maintenance mode",
        action: async (repository: Repository, value: boolean) => {
            const config = await repository.MaintenanceConfiguration.get();
            config.IsInMaintenanceMode = value;
            return await repository.MaintenanceConfiguration.modify(config);
        },
        afterComplete: async (_repository: Repository, updatedConfig: MaintenanceConfigurationResource) => {
            setConfiguration(updatedConfig);
        },
    });
    const toggleMaintenanceModePageAction: PrimaryPageAction = {
        type: "button",
        label: configuration.IsInMaintenanceMode ? "Disable maintenance mode" : "Enable maintenance mode",
        busyLabel: configuration.IsInMaintenanceMode ? "Disabling..." : "Enabling...",
        disabled: isExecuting,
        onClick: () => toggleMaintenanceMode(!configuration.IsInMaintenanceMode),
    };
    return (<PageContent header={{ title: "Maintenance", primaryAction: toggleMaintenanceModePageAction }}>
            <Section>
                <Note>
                    When maintenance mode is enabled, users with the <strong>Administer System</strong> permission will be able to make changes and initiate deployments, just like normal. All other users will be prevented from making changes or
                    starting new tasks. Learn about <ExternalLink href="MaintenanceMode">maintenance mode</ExternalLink>.
                </Note>
                <p>
                    Maintenance mode is currently <strong>{configuration.IsInMaintenanceMode ? "Enabled" : "Disabled"}</strong>
                </p>
            </Section>
        </PageContent>);
}
